// Client Executive Summary — Library / repository panel.
//
// Lists every saved executive summary, shared across the sales team. Each
// row exposes three actions:
//   - View PDF: opens the rendered PDF in a new tab
//   - Edit: hands the summary id back to the parent so the wizard tab
//     rehydrates the payload and jumps into edit mode
//   - Delete: hard-delete (with confirm) — clears the DB row and the PDF
//
// Search + sort so Dawn/Silvia can find a summary fast when they need to
// go back and fix imagery, narrative, or a proposal update.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const NAVY = '#1A5490';

function formatCurrency(n) {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function SummaryLibrary({ onEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, client }
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setErr(null);
    api.get('/exec-summary/list')
      .then(({ data }) => setItems((data && data.items) || []))
      .catch((e) => setErr(e?.response?.data?.error || e?.message || 'Failed to load summaries.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? items.filter((it) => {
          const hay = [
            it.client, it.project_name, it.address_line1, it.address_line2,
            it.created_by_name, it.created_by_email,
          ].filter(Boolean).join(' ').toLowerCase();
          return hay.includes(q);
        })
      : items.slice();
    rows.sort((a, b) => {
      let cmp;
      if (sortField === 'grand_total') {
        cmp = (Number(a.grand_total) || 0) - (Number(b.grand_total) || 0);
      } else if (sortField === 'updated_at' || sortField === 'created_at') {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
      } else {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [items, search, sortField, sortDir]);

  const handleView = async (id) => {
    try {
      const res = await api.get(`/exec-summary/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      // Open in new tab; revoke shortly after so the tab has time to load.
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let msg = e?.response?.data?.error || e?.message || 'Download failed.';
      // When responseType is blob, error payloads come back as Blob too.
      const blob = e?.response?.data;
      if (blob && typeof blob.text === 'function') {
        try {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).error || msg; } catch { msg = txt || msg; }
        } catch {}
      }
      setErr(msg);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/exec-summary/${confirmDelete.id}`);
      setConfirmDelete(null);
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const COLUMNS = [
    { field: 'client', label: 'Client' },
    { field: 'project_name', label: 'Project' },
    { field: 'created_by_name', label: 'Created by' },
    { field: 'grand_total', label: 'Grand Total', align: 'right' },
    { field: 'updated_at', label: 'Updated' },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
            Executive summary library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every deck the sales team has generated. Open the PDF, edit the source, or delete.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search client, project, address, salesperson…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Tooltip title="Refresh">
          <IconButton onClick={refresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: NAVY }} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c.field} align={c.align || 'left'}>
                    <TableSortLabel
                      active={sortField === c.field}
                      direction={sortField === c.field ? sortDir : 'asc'}
                      onClick={() => handleSort(c.field)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{it.client || '—'}</Typography>
                    {it.address_line1 && (
                      <Typography variant="caption" color="text.secondary">
                        {it.address_line1}{it.address_line2 ? `, ${it.address_line2}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{it.project_name || '—'}</TableCell>
                  <TableCell>{it.created_by_name || '—'}</TableCell>
                  <TableCell align="right">{formatCurrency(it.grand_total)}</TableCell>
                  <TableCell>{formatDate(it.updated_at)}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View PDF">
                      <IconButton size="small" onClick={() => handleView(it.id)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit in wizard">
                      <IconButton size="small" onClick={() => onEdit && onEdit(it.id)} sx={{ color: NAVY }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setConfirmDelete({ id: it.id, client: it.client })} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                    {search ? 'No summaries match your search.' : 'No summaries yet. Generate one from the wizard.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!confirmDelete} onClose={() => !deleting && setConfirmDelete(null)}>
        <DialogTitle>Delete executive summary?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the deck for{' '}
            <b>{(confirmDelete && confirmDelete.client) || 'this client'}</b> from the shared library,
            including the generated PDF. Sales team members with the current link will no longer be
            able to download it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting} sx={{ textTransform: 'none' }}>
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
